import {type Descendant} from 'slate';
import {create} from 'zustand';
import {createJSONStorage, devtools, persist} from 'zustand/middleware';

import {safeLocalStorage} from '../../util/localStorage';
import {
  COMPOSER_INITIAL_VALUE,
  DEFAULT_DRAWER_WIDTH,
  DEFAULT_WINDOW_HEIGHT,
  DEFAULT_WINDOW_WIDTH,
} from './constants';

const wbAgentStorage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = {
  getItem: name => safeLocalStorage.getItem(name),
  setItem: (name, value) => {
    try {
      safeLocalStorage.setItem(name, value);
    } catch (e) {
      if (
        e instanceof DOMException &&
        (e.code === 22 || e.name === 'QuotaExceededError')
      ) {
        console.warn(`localStorage quota exceeded for "${name}"`, e);
        return;
      }
      console.warn(`Failed to persist "${name}" to localStorage`, e);
    }
  },
  removeItem: name => safeLocalStorage.removeItem(name),
};

export interface SelectedRun {
  name: string;
  displayName: string;
}

interface WindowPosition {
  x: number;
  y: number;
}

interface WBAgentContextState {
  // Agent chat open state
  open: boolean;
  // Root turn id to reopen on load, or null to stay closed.
  restoreThreadId: string | null;
  // Docked state
  docked: boolean;
  // Drawer width
  drawerWidth: number;
  // Window geometry (persists across open/close and dock/undock)
  windowPosition: WindowPosition | null;
  windowWidth: number;
  windowHeight: number;
  // Composer contents
  composerDraft: Descendant[];
  // Run context
  selectedRuns: SelectedRun[];
  projectName: string | undefined;
  // History sidebar
  historySidebarOpen: boolean;

  // Actions
  setOpen: (open: boolean) => void;
  setRestoreThreadId: (id: string | null) => void;
  setDocked: (docked: boolean) => void;
  setDrawerWidth: (width: number) => void;
  setWindowPosition: (pos: WindowPosition) => void;
  setWindowSize: (width: number, height: number) => void;
  setComposerDraft: (value: Descendant[]) => void;
  addRun: (run: SelectedRun) => void;
  removeRun: (name: string) => void;
  clearRuns: () => void;
  syncProject: (projectName: string | undefined) => void;
  setHistorySidebarOpen: (open: boolean) => void;
}

/* oxlint-disable wandb/zustand-enforce-slices-when-large-state -- store pre-dates rule; slicing tracked in follow-up */
export const useWBAgentContextStore = create<WBAgentContextState>()(
  devtools(
    persist(
      set => ({
        open: false,
        restoreThreadId: null,
        docked: true,
        drawerWidth: DEFAULT_DRAWER_WIDTH,
        windowPosition: null,
        windowWidth: DEFAULT_WINDOW_WIDTH,
        windowHeight: DEFAULT_WINDOW_HEIGHT,
        composerDraft: COMPOSER_INITIAL_VALUE,
        selectedRuns: [],
        projectName: undefined,
        historySidebarOpen: false,

        setOpen: open => set({open}),
        setRestoreThreadId: id => set({restoreThreadId: id}),
        setDocked: docked => set({docked}),
        setDrawerWidth: width => set({drawerWidth: width}),
        setWindowPosition: pos => set({windowPosition: pos}),
        setWindowSize: (width, height) =>
          set({windowWidth: width, windowHeight: height}),
        setComposerDraft: value => set({composerDraft: value}),
        addRun: run =>
          set(state => {
            if (state.selectedRuns.some(r => r.name === run.name)) {
              return state;
            }
            return {selectedRuns: [...state.selectedRuns, run]};
          }),
        removeRun: name =>
          set(state => ({
            selectedRuns: state.selectedRuns.filter(r => r.name !== name),
          })),
        clearRuns: () => set({selectedRuns: []}),
        syncProject: projectName =>
          set(state => {
            if (state.projectName === projectName) {
              return state;
            }
            return {projectName, selectedRuns: []};
          }),
        setHistorySidebarOpen: historySidebarOpen => set({historySidebarOpen}),
      }),
      {
        name: 'wb-agent-window-preferences',
        storage: createJSONStorage(() => wbAgentStorage),
        version: 1,
        // v0 persisted `open`, which reopened an empty chat on every load.
        migrate: (persistedState, version) => {
          const state = persistedState as WBAgentContextState | null;
          if (version === 0 && state != null) {
            delete (state as Partial<WBAgentContextState>).open;
          }

          return state as WBAgentContextState;
        },
        partialize: state => ({
          restoreThreadId: state.restoreThreadId,
          docked: state.docked,
          drawerWidth: state.drawerWidth,
          windowPosition: state.windowPosition,
          windowWidth: state.windowWidth,
          windowHeight: state.windowHeight,
          historySidebarOpen: state.historySidebarOpen,
        }),
      }
    ),
    {name: 'WBAgentContextStore'}
  )
);
